// eslint-disable-next-line jira/restricted/react-component-props
import React, { useEffect, useCallback, type ComponentProps } from 'react';
import { styled } from '@compiled/react';
import flow from 'lodash/flow';
import { AnalyticsSource as AnalyticsSourceDecorator } from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import { AGILITY_BACKLOG } from '@atlassian/jira-common-constants/src/analytics-sources';
import { isAgilityBoard } from '@atlassian/jira-common-is-agility-board';
import { getLongTasksMetrics } from '@atlassian/jira-common-long-task-metrics';
import { bentoReporter } from '@atlassian/jira-common-long-task-metrics/src/reporters/software-bento';
import {
	simpleSoftwareBoardConfiguration,
	softwareConfiguration,
} from '@atlassian/jira-issue-view-configurations';
import type { ExternalAction } from '@atlassian/jira-issue-view-store/src/actions/external-actions';
import { IssueBoundary } from '@atlassian/jira-issue-view/src/async.tsx';
import type IssueAppType from '@atlassian/jira-issue-view/src/views/issue-details/issue-app';
import { SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import type { BrowserHistory } from '@atlassian/react-resource-router';
import { BACKLOG_PACKAGE_NAME } from '../../../common/constants/analytics';
import { getBacklogCardCount } from '../../../services/performance/backlog-data-collector';
import {
	setIssueViewOpenFeedbackMark,
	setIssueViewOpenFinishMark,
} from '../../../services/performance/issue-view-metrics';
import { useBacklogSelector } from '../../../state';
import { useIsCMPBacklog } from '../../../state/state-hooks/capabilities';
import { PreIssueViewMountFeedbackMark } from '../../metrics/issue-view';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const IssueApp = lazyForPaint<typeof IssueAppType>(
	() =>
		import(
			/* webpackChunkName: "async-issue-app" */ '@atlassian/jira-issue-view/src/views/issue-details/issue-app'
		),
	{ ssr: false },
);

type IssueAppConfig = JSX.LibraryManagedAttributes<
	typeof IssueAppType,
	ComponentProps<typeof IssueAppType>
>;

export type Props = {
	issueKey: IssueAppConfig['issueKey'];
	isAgilityBoard: boolean;
	isLoadedWithPage: boolean;
	rapidViewId: IssueAppConfig['rapidViewId'];
	history: BrowserHistory | null;
	externalAction: ExternalAction | undefined;
	backlogSessionId: string;
	onClose: IssueAppConfig['onClose'];
	onChange: IssueAppConfig['onChange'];
	onIssueDeleteSuccess: IssueAppConfig['onIssueDeleteSuccess'];
	onIssueKeyChange: IssueAppConfig['onIssueKeyChange'];
};

const IssueWrapper = (props: Props) => {
	const backlogCardCount = useBacklogSelector(getBacklogCardCount);
	const isCompanyManaged = useIsCMPBacklog();
	// If there's an external action to be fired, dispatch it as soon as possible
	const onSetDispatchExternalActionRef = useCallback(
		(dispatchExternalActionRef: ((arg1: ExternalAction) => void) | null) => {
			if (dispatchExternalActionRef && props.externalAction) {
				dispatchExternalActionRef(props.externalAction);
			}
		},
		[props.externalAction],
	);

	useEffect(() => {
		setIssueViewOpenFeedbackMark();
	}, [props.issueKey]);

	const isAgilityBoardForIssue =
		props.isAgilityBoard !== undefined ? props.isAgilityBoard : isAgilityBoard;

	const configWithAppKey = isAgilityBoardForIssue
		? simpleSoftwareBoardConfiguration()
		: softwareConfiguration();
	const { applicationKey, ...configWithoutAppKey } = configWithAppKey;

	return (
		<BacklogIssueContainer data-testid="software-backlog.detail-view.issue-wrapper.backlog-issue">
			<PreIssueViewMountFeedbackMark issueKey={props.issueKey} />
			<IssueBoundary packageName={BACKLOG_PACKAGE_NAME}>
				<IssueApp
					{...configWithoutAppKey}
					analyticsSource={AGILITY_BACKLOG}
					isLoadedWithPage
					issueKey={props.issueKey}
					onChange={props.onChange}
					onClose={props.onClose}
					rapidViewId={props.rapidViewId}
					viewModeOptions={{
						viewModeSwitchEnabled: false,
						viewMode: 'SIDEBAR',
					}}
					onIssueDeleteSuccess={props.onIssueDeleteSuccess}
					shouldSetInitialFocus
					shouldShowCloseButton
					metrics={{
						onReadyForUser: () => {
							getLongTasksMetrics('bento').stop(bentoReporter, {
								type: 'sidebar',
								backlogSessionId: props.backlogSessionId,
							});
						},
						onInteractive: () => {
							setIssueViewOpenFinishMark(true, backlogCardCount, isCompanyManaged);
						},
					}}
					onIssueKeyChange={props.onIssueKeyChange}
					history={null}
					dispatchExternalActionRef={onSetDispatchExternalActionRef}
				/>
			</IssueBoundary>
		</BacklogIssueContainer>
	);
};

export default flow(AnalyticsSourceDecorator(AGILITY_BACKLOG, SCREEN))(IssueWrapper);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BacklogIssueContainer = styled.div({
	height: '100%',
});
